import React, { useEffect } from 'react'
import { Appearance, Platform } from 'react-native'
import * as eva from '@eva-design/eva'
import { EvaIconsPack } from '@ui-kitten/eva-icons'
import { ApplicationProvider, IconRegistry } from '@ui-kitten/components'
import * as Notifications from 'expo-notifications'
import { useAtomValue, useSetAtom } from 'jotai'
import ContextWrapper from './global'
import { resolvedThemeAtom, systemColorSchemeAtom } from './global/AtomsProvider'
import './localization/i18n'
import { customDarkTheme } from './theme/customDarkTheme'
import { useKeepAwakeSafely } from './utils/useKeepAwakeSafely'
import * as Sentry from '@sentry/react-native'
import { ToastProvider } from 'react-native-toast-notifications'
import 'text-encoding-polyfill' // for PostHog to work on iOS
import * as Updates from 'expo-updates'
import appJson from './app.json'
import { buildSentryRelease, buildSentryDist } from './utils/sentryRelease'
import { SENTRY_IGNORE_ERRORS, SENTRY_REPORTABLE_POSTHOG_EVENT, createSentryBeforeSend } from './utils/sentryFilters'
import { captureOperationalEvent } from './global/analytics/operationalTelemetry'
import { resolveClientEnv } from './utils/clientEnvironment'
import { auth } from './global/Firebase'
import { startDualWriteOutboxAutoDrain } from './global/Firebase/dualWriteOutbox'
import { startMirrorIntentRecovery } from './global/Firebase/mirrorIntent'
import { startWriteClaimPool } from './global/Firebase/writeClaims'

const sentryUpdateId = Platform.OS === 'web' ? undefined : Updates.updateId

const sentryRelease = buildSentryRelease({
  version: appJson.expo.version,
  updateId: sentryUpdateId,
  platform: Platform.OS,
})

// ASO-AN-003: `dist` used to be the literal `Platform.OS` — which the release suffix already
// says, so the field named nothing — and it was maintained as a SECOND literal in
// publish-ota.js, two strings that had to agree forever or symbolication silently breaks. It is
// now the EAS update id (the thing a sourcemap actually belongs to, and the same value the
// uploader tags with), falling back to the native build number when there is no update id.
// Both sides come from the shared helper, so they cannot drift.
const sentryDist = buildSentryDist({
  platform: Platform.OS,
  updateId: sentryUpdateId,
  buildNumber: appJson.expo.ios?.buildNumber,
  versionCode: appJson.expo.android?.versionCode,
})

Sentry.init({
  dsn: 'https://970a4159214648a5aa6b7ceae4235032@o1236871.ingest.sentry.io/6387172',
  debug: false,
  release: sentryRelease,
  dist: sentryDist,
  // Was `process.env.MY_ENVIRONMENT || 'production'`, which is inert in a shipped bundle — only
  // EXPO_PUBLIC_* is inlined — so every staging build has been reporting its crashes to Sentry as
  // `production`. Same defect, same fix, one shared resolver. See utils/clientEnvironment.js.
  environment: resolveClientEnv(),
  enableAutoSessionTracking: true,
  sessionTrackingIntervalMillis: 10000,
  tracesSampleRate: 0.05,
  // ATL-563: drop expected POS noise (offline network errors, wake-lock denials,
  // form-validation messages, transient pre-auth permission-denied) before send.
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  // ATL-2035: Sentry carries write failures and crashes only, one per (signature, device) per
  // hour; EVERY occurrence — sent, held back as a repeat, or filtered — is counted in PostHog
  // (grouped by organization), so the quota no longer decides what we can see.
  beforeSend: createSentryBeforeSend({
    isAuthenticated: () => auth?.currentUser != null,
    onReportableEvent: (properties) => captureOperationalEvent(SENTRY_REPORTABLE_POSTHOG_EVENT, properties),
  }),
})

// ATL-983: give the dual-write outbox a drain trigger that does not require a dual-write to
// happen first. Every previous trigger lived inside `bestEffortDualWrite`, so a till that queued
// failed writes and then simply did not sell again never opened its queue and the entries aged
// out unattempted. Fire-and-forget and total by construction — it must never delay or break boot.
// Placed after Sentry.init so its own failures are reportable.
startDualWriteOutboxAutoDrain()
// ATL-1459: a Firestore write whose app died before its mirror ran left a persisted intent; once the
// session knows its organization, Firestore is asked whether the commit landed and, if it did, the
// mirror is queued in the outbox started above. Same contract: never delays or breaks boot.
startMirrorIntentRecovery()
// ATL-2004: mint (online) and load the session organization's pool of server-acknowledged write
// claims, which make a re-delivered sale batch refusable. Dark unless `write_claim_org_allowlist`
// names the org. Same contract: never delays or breaks boot.
startWriteClaimPool()

function App() {
  useKeepAwakeSafely()

  const resolvedTheme = useAtomValue(resolvedThemeAtom)
  const setSystemColorScheme = useSetAtom(systemColorSchemeAtom)

  useEffect(() => {
    const colorScheme = Appearance.getColorScheme()
    setSystemColorScheme(colorScheme === 'dark' ? 'dark' : 'light')

    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setSystemColorScheme(colorScheme === 'dark' ? 'dark' : 'light')
    })
    return () => subscription.remove()
  }, [setSystemColorScheme])

  useEffect(() => {
    Notifications.setNotificationHandler({
      handleNotification: async () => ({
        shouldShowAlert: true,
        shouldPlaySound: false,
        shouldSetBadge: false,
      }),
    })
    // if (Platform.OS !== 'web') {
    //   RNUxcam?.optIntoSchematicRecordings(); // Add this line to enable iOS screen recordings 
    //   const configuration = { 
    //     userAppKey: '2z7wul94vqsqmpn', 
    //     enableAutomaticScreenNameTagging: false, 
    //     enableAdvancedGestureRecognition: false, // default is true 
    //     enableImprovedScreenCapture: true, // for improved screen capture on Android 
    //   } 
    //   RNUxcam?.startWithConfiguration(configuration)
    // }
  }, [])
  
  return (
    <ToastProvider>
      <IconRegistry icons={EvaIconsPack} />
      <ApplicationProvider {...eva} theme={resolvedTheme === 'dark' ? { ...eva.dark, ...customDarkTheme } : eva.light}>
        <ContextWrapper />
      </ApplicationProvider>
    </ToastProvider>
  )
}

export default Sentry.wrap(App)
